/**
 * @fileoverview Tabs - Tabbed content panels
 *
 * Purpose: Organize content into switchable tabs
 * Dependencies: Radix UI Tabs primitives
 * Used by: Settings panels, dashboards, multi-view interfaces
 *
 * Components exported:
 * - Tabs: Root tabs container
 * - TabsList: Tab buttons container
 * - TabsTrigger: Individual tab button
 * - TabsContent: Tab panel content
 *
 * Features:
 * - Keyboard navigation (arrow keys, Home, End)
 * - Accessible (ARIA attributes, roles)
 * - Smooth content switching
 * - Controlled or uncontrolled
 *
 * Variants:
 * - `default` — filled pill container, for standalone tab groups.
 * - `underline` — borderless triggers on a bottom rule, for tabbed headers
 *   that sit flush under a title bar (the review / adjudication workspaces).
 *   Set `variant` on both `TabsList` and its `TabsTrigger`s.
 *
 * @example
 * <Tabs defaultValue="account">
 *   <TabsList>
 *     <TabsTrigger value="account">Account</TabsTrigger>
 *     <TabsTrigger value="password">Password</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="account">
 *     Account settings here
 *   </TabsContent>
 *   <TabsContent value="password">
 *     Password settings here
 *   </TabsContent>
 * </Tabs>
 */

"use client";

import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/utils/misc";

const Tabs = TabsPrimitive.Root;

const tabsListVariants = cva("inline-flex items-center text-muted-foreground", {
  variants: {
    variant: {
      default: "h-10 justify-center rounded-md bg-muted p-1",
      underline:
        "h-auto justify-start gap-4 rounded-none border-b bg-transparent px-5 py-0",
    },
  },
  defaultVariants: { variant: "default" },
});

const tabsTriggerVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:text-foreground",
  {
    variants: {
      variant: {
        default:
          "rounded-sm px-3 py-1.5 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm",
        underline:
          "gap-1.5 rounded-none border-b-2 border-transparent px-0 pb-2.5 pt-2 text-xs data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> &
    VariantProps<typeof tabsListVariants>
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(tabsListVariants({ variant }), className)}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> &
    VariantProps<typeof tabsTriggerVariants>
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(tabsTriggerVariants({ variant }), className)}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
